import DeleteModal from "@/components/global/modals/delete_modal.vue";
import ConfirmationLeaveModal from "@/components/global/globalModals/confirmation_leave_modal.vue";
import ConfirmationModal from "@/components/global/globalModals/confirmation_modal.vue";

export default function initModalManager(Vue) {
  Vue.prototype.$egModal = {
    _data: Vue.observable({
      modals: [],
    }),
    _registeredModals: {},

    unregisterModal(modalId) {
      delete this._registeredModals[modalId];
      this._data.modals = this._data.modals.filter(
        (modal) => modal.id !== modalId
      );
    },

    registerModal(modalId, componentInstance) {
      this._registeredModals[modalId] = componentInstance;
    },

    show(modalId, args) {
      const {
        component,
        context,
        propsKeys = [],
        props = {},
        on = {},
        scopedSlots = {},
      } = args;

      if (this._registeredModals[modalId]) {
        const modal = this._registeredModals[modalId];

        modal.show();

        return;
      }

      return new Promise((resolve) => {
        if (!component) {
          throw new Error(`Component for modal ${modalId} is not defined`);
        }

        const allProxyProps = [
          //TODO: after release 4.0 temporary solution,
          // need to find all places where the duplicates appears.
          ...new Set([...propsKeys, ...Object.keys(props), "id", "resolve"]),
        ];
        const proxyProps = new Proxy(
          { ...props, id: modalId, resolve },
          {
            get(target, prop) {
              if (allProxyProps.includes(prop)) {
                if (prop === "id") {
                  return target[prop];
                }

                if (prop === "resolve") {
                  return target[prop];
                }

                if (props[prop] !== undefined) {
                  return props[prop];
                }

                if (context && context[prop] !== undefined) {
                  return context[prop];
                }

                return undefined;
              }
            },

            set(_, prop) {
              throw new Error(`Property ${prop} is read-only`);
            },

            ownKeys() {
              return allProxyProps;
            },

            getOwnPropertyDescriptor(_, prop) {
              if (allProxyProps.includes(prop)) {
                return {
                  enumerable: true,
                  configurable: true,
                };
              }
            },
          }
        );

        this._data.modals.push({
          id: modalId,
          component,
          props: proxyProps,
          on,
          ref: `modal-${modalId}`,
          // Avoid re-rendering the scoped slot function every time when modals is changed
          scopedSlots: Object.entries(scopedSlots).reduce((acc, [key, fn]) => {
            const memoCache = new Map();

            acc[key] = (slotProps) => {
              const memoKey = fn;

              if (!memoCache.has(memoKey)) {
                // Generate the scoped slot once and store it
                const result = fn(slotProps);
                memoCache.set(memoKey, result);
              }

              // Return the cached result
              return memoCache.get(memoKey);
            };

            return acc;
          }, {}),
        });

        Vue.nextTick(() => {
          const modal = this._registeredModals[modalId];

          if (!modal) {
            throw new Error(
              `Modal ${modalId} should has eg-modal component as root component`
            );
          }

          modal.show();
          modal.$once("close", () => {
            resolve(false);

            this._data.modals = this._data.modals.filter(
              (modal) => modal.id !== modalId
            );
            // NOTE: unregister called automatically from component on beforeDestroy hook
          });
        });
      });
    },

    async hide(modalId) {
      const indexInData = this._data.modals.findIndex(
        (modal) => modal.id === modalId
      );

      if (this._registeredModals[modalId]) {
        await this._registeredModals[modalId].silentHide();

        if (indexInData !== -1) {
          this.unregisterModal(modalId);
        }
      }

      if (indexInData !== -1) {
        this._data.modals.splice(indexInData, 1);
      }
    },

    hideAll() {
      return Promise.all(
        this._data.modals.map((modal) => {
          return this.hide(modal.id);
        })
      );
    },
  };

  Vue.prototype.$showDeleteConfirmationModal = (props = {}) => {
    return Vue.prototype.$egModal.show("delete-modal", {
      component: DeleteModal,
      props,
    });
  };

  Vue.prototype.$showConfirmationLeaveModal = (props = {}) => {
    return Vue.prototype.$egModal.show("confirmation-leave-modal", {
      component: ConfirmationLeaveModal,
      props,
    });
  };
  Vue.prototype.$showConfirmationModal = (props = {}) => {
    return Vue.prototype.$egModal.show("confirmation-modal", {
      component: ConfirmationModal,
      props,
    });
  };
}
